import { useState, useEffect, useCallback } from "react";
import { Heart, ChevronDown, ChevronUp, Phone, Handshake, Target, Brain, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getScoreColor(score) {
  if (score >= 75) return "text-emerald-400";
  if (score >= 50) return "text-amber-400";
  if (score >= 25) return "text-orange-400";
  return "text-red-400";
}

function getScoreBg(score) {
  if (score >= 75) return "bg-emerald-500";
  if (score >= 50) return "bg-amber-500";
  if (score >= 25) return "bg-orange-500";
  return "bg-red-500";
}

function getScoreLabel(score) {
  if (score >= 75) return "Excellent";
  if (score >= 50) return "Good";
  if (score >= 25) return "Needs Work";
  return "Critical";
}

function ScoreBar({ label, score, icon: Icon }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-3.5 h-3.5 text-gray-500 shrink-0" />
      <span className="text-xs text-gray-400 w-16 shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${getScoreBg(score)}`}
          style={{ width: `${score}%` }}
        />
      </div>
      <span className={`text-xs font-mono w-8 text-right ${getScoreColor(score)}`}>{score}</span>
    </div>
  );
}

export function TeamHealthScores() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTeam, setExpandedTeam] = useState(null);

  const fetchHealthScores = useCallback(async () => {
    try {
      const response = await fetch(`${API}/teams/health-scores?days=30`, { credentials: "include" });
      if (response.ok) {
        setTeams(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch health scores:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealthScores();
  }, [fetchHealthScores]);

  if (loading) {
    return (
      <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5" data-testid="team-health-scores-loading">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <Heart className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="h-5 w-40 bg-white/10 rounded animate-pulse" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-white/5 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5" data-testid="team-health-scores-empty">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <Heart className="w-5 h-5 text-emerald-400" />
          </div>
          <h3 className="text-white font-semibold">Team Health Scores</h3>
        </div>
        <p className="text-gray-500 text-sm text-center py-4">Create teams and add members to see health scores.</p>
      </div>
    );
  }

  return (
    <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5" data-testid="team-health-scores">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
          <Heart className="w-5 h-5 text-emerald-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold font-heading">Team Health Scores</h3>
          <p className="text-xs text-gray-500">Last 30 days — Activity, Deals, Quotas, Coaching</p>
        </div>
      </div>

      <div className="space-y-2">
        {teams.map((team) => {
          const isExpanded = expandedTeam === team.team_id;
          return (
            <div key={team.team_id} data-testid={`health-score-${team.team_id}`}>
              <button
                onClick={() => setExpandedTeam(isExpanded ? null : team.team_id)}
                className="w-full flex items-center gap-3 p-3 rounded-lg bg-white/[0.03] hover:bg-white/[0.06] transition-colors group"
              >
                {/* Score circle */}
                <div className="relative w-12 h-12 shrink-0">
                  <svg className="w-12 h-12 -rotate-90" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="3" />
                    <circle
                      cx="18" cy="18" r="16" fill="none"
                      strokeWidth="3"
                      strokeDasharray={`${team.health_score} ${100 - team.health_score}`}
                      strokeLinecap="round"
                      className={team.health_score >= 75 ? "stroke-emerald-400" : team.health_score >= 50 ? "stroke-amber-400" : team.health_score >= 25 ? "stroke-orange-400" : "stroke-red-400"}
                    />
                  </svg>
                  <span className={`absolute inset-0 flex items-center justify-center text-sm font-bold ${getScoreColor(team.health_score)}`}>
                    {team.health_score}
                  </span>
                </div>

                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium text-sm truncate">{team.team_name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      team.health_score >= 75 ? "bg-emerald-500/20 text-emerald-400" :
                      team.health_score >= 50 ? "bg-amber-500/20 text-amber-400" :
                      team.health_score >= 25 ? "bg-orange-500/20 text-orange-400" :
                      "bg-red-500/20 text-red-400"
                    }`}>
                      {getScoreLabel(team.health_score)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-[10px] text-gray-500">
                      <Users className="w-3 h-3 inline mr-0.5" />{team.member_count} members
                    </span>
                    {team.breakdown?.total_cash > 0 && (
                      <span className="text-[10px] text-emerald-400/70">
                        ${team.breakdown.total_cash.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>

                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-500 group-hover:text-gray-300" />
                )}
              </button>

              {isExpanded && (
                <div className="mt-1 ml-3 mr-3 p-3 rounded-lg bg-black/20 border border-white/5 space-y-2.5 animate-in slide-in-from-top-1">
                  <ScoreBar label="Activity" score={team.activity_score} icon={Phone} />
                  <ScoreBar label="Deals" score={team.deal_score} icon={Handshake} />
                  <ScoreBar label="Quota" score={team.quota_score} icon={Target} />
                  <ScoreBar label="Coaching" score={team.coaching_score} icon={Brain} />

                  {team.breakdown && (
                    <div className="pt-2 border-t border-white/5 grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                      <div className="text-gray-500">Avg dials/day/rep: <span className="text-gray-300">{team.breakdown.avg_daily_dials_per_rep}</span></div>
                      <div className="text-gray-500">Close rate: <span className="text-gray-300">{team.breakdown.close_rate}%</span></div>
                      <div className="text-gray-500">Deals closed: <span className="text-gray-300">{team.breakdown.deal_count}</span></div>
                      <div className="text-gray-500">Quota att.: <span className="text-gray-300">{team.breakdown.quota_attainment}%</span></div>
                      <div className="text-gray-500">Coaching avg: <span className="text-gray-300">{team.breakdown.avg_coaching_score}</span></div>
                      <div className="text-gray-500">Calls analyzed: <span className="text-gray-300">{team.breakdown.analyzed_calls}</span></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
